/**
 * Slider — Frontend view script (view.js).
 *
 * Initialises Splide.js on every .waiis-slider element present in the page.
 * Configuration is read from data-* attributes written by render.php.
 *
 * @package waiis
 */

import Splide from '@splidejs/splide';
import { AutoScroll } from '@splidejs/splide-extension-auto-scroll';

/**
 * A11Y: convierte la paginación de Splide (que usa `role="tablist"`/`tab`)
 * en botones independientes, ya que no existen los `tabpanel` correspondientes.
 * Sin esto, los lectores de pantalla anuncian pestañas huérfanas.
 *
 * @param {HTMLElement} el Elemento raíz del slider.
 */
function normalisePagination( el ) {
	const pagination = el.querySelector( '.splide__pagination' );
	if ( ! pagination ) return;

	// Quitar el rol de listado de pestañas del contenedor si lo tiene.
	if ( pagination.getAttribute( 'role' ) === 'tablist' ) {
		pagination.setAttribute( 'role', 'group' );
	}

	pagination.querySelectorAll( '.splide__pagination__page' ).forEach( function ( page ) {
		// Idempotente: sólo escribimos cuando el valor difiere. Evita disparar
		// el MutationObserver y re-pintar DOM cuando ya está correcto.
		if ( page.getAttribute( 'role' ) !== 'button' ) {
			page.setAttribute( 'role', 'button' );
		}
		if ( page.hasAttribute( 'aria-selected' ) ) {
			page.removeAttribute( 'aria-selected' );
		}
	} );
}

function initSliders() {
	/** @type {NodeListOf<HTMLElement>} */
	const sliderEls = document.querySelectorAll( '.waiis-slider' );

	sliderEls.forEach( ( el ) => {
		const perPage           = parseInt( el.dataset.perPage, 10 )       || 3;
		const perPageTablet     = parseInt( el.dataset.perPageTablet, 10 ) || 2;
		const perPageMobile     = parseInt( el.dataset.perPageMobile, 10 ) || 1;
		const gap               = el.dataset.gap                            || '1.5rem';
		const fixedWidthPx      = parseInt( el.dataset.fixedWidth, 10 )    || 350;
		const fixedWidthEnabled = el.dataset.fixedWidthEnabled !== 'false';
		const startIndex        = parseInt( el.dataset.startIndex, 10 )    || 0;
		const autoplay      = el.dataset.autoplay === 'true';
		const speed         = parseInt( el.dataset.speed, 10 )         || 400;
		const useLoop       = el.dataset.rewind !== 'false';
		const focusVal      = el.dataset.focus      || 'center';
		const trimSpaceVal  = el.dataset.trimSpace  !== 'false';

		const ticker      = el.dataset.ticker === 'true';
		const tickerSpeed = parseFloat( el.dataset.tickerSpeed ) || 2;

		// WCAG 2.2.2 / 2.3.3 — respetar la preferencia de "reducir movimiento".
		// En tickers (auto-scroll continuo) evitamos el desplazamiento automático.
		const prefersReducedMotion = window.matchMedia &&
			window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

		// A11Y: i18n compartido por todos los modos (evita textos en inglés).
		const i18n = {
			prev      : 'Anterior diapositiva',
			next      : 'Siguiente diapositiva',
			first     : 'Ir a la primera diapositiva',
			last      : 'Ir a la última diapositiva',
			slideX    : 'Ir a la diapositiva %s',
			pageX     : 'Ir a la página %s',
			carousel  : 'carrusel',
			slide     : 'diapositiva',
			select    : 'Paginación del carrusel',
			slideLabel: '%s de %s',
		};

		const splide = new Splide( el, ticker ? {
			// ── Ticker / continuous auto-scroll mode ──────────────────────────
			type       : 'loop',
			drag       : 'free',
			focus      : 0,
			perPage,
			perMove    : 1,
			gap,
			arrows     : false,
			pagination : false,
			// ── A11Y: force correct ARIA roles natively ───────────────────────
			role       : 'group',
			slideFocus : false,
			autoScroll : prefersReducedMotion
				? false // WCAG 2.2.2 / 2.3.3: sin auto-scroll si el usuario pide reducir movimiento.
				: {
					speed        : tickerSpeed,
					// El hover/focus se gestiona solo en la pista de slides (no sobre
					// el root que también contiene el botón de control).
					pauseOnHover : false,
					pauseOnFocus : false,
				},
			fixedWidth : fixedWidthEnabled ? fixedWidthPx + 'px' : false,
			i18n,
			breakpoints: {
				1024: { perPage: perPageTablet },
				768 : { perPage: perPageMobile, gap: '1rem', fixedWidth: false },
			},
		} : {
			type  : useLoop ? 'loop' : 'slide',
			rewind: ! useLoop,
			fixedWidth: fixedWidthEnabled ? fixedWidthPx + 'px' : false,
			start  : startIndex,

			perPage,
			gap,
			speed,
			focus    : focusVal,
			trimSpace: trimSpaceVal,

			autoplay,
			pauseOnHover: true,
			pauseOnFocus: true,

			accessibility: true,
			// ── A11Y: force correct ARIA roles natively ───────────────────────
			role      : 'group',
			slideFocus: false,

			i18n: {
				prev      : 'Anterior diapositiva',
				next      : 'Siguiente diapositiva',
				first     : 'Ir a la primera diapositiva',
				last      : 'Ir a la última diapositiva',
				slideX    : 'Ir a la diapositiva %s',
				pageX     : 'Ir a la página %s',
				carousel  : 'carrusel',
				slide     : 'diapositiva',
				select    : 'Paginación del carrusel',
				slideLabel: '%s de %s',
			},

			reducedMotion: {
				speed   : 0,
				rewindSpeed: 0,
				autoplay: false,
			},

			breakpoints: {
				1024: {
					perPage: perPageTablet,
				},
				768: {
					perPage   : perPageMobile,
					gap       : '1rem',
					start     : 0,
					fixedWidth: false,
				},
			},
		} );

		// ── A11Y: fix slide role ──────────────────────────────────────────────
		// Splide assigns role="tabpanel" to <li> elements by default, which is
		// semantically invalid inside a <ul> without a full tab-widget structure.
		// We override it with role="group" + aria-roledescription="slide" on
		// every mount and every navigation so cloned slides are also patched.
		//
		// PERFORMANCE: reescribir atributos en TODAS las slides (incluidas las
		// clonadas de loop) en CADA evento "moved" provoca una tormenta de
		// escrituras DOM por frame, especialmente en los tickers (AutoScroll),
		// cuyo move() corre en cada requestAnimationFrame. Ahora:
		//  - "mounted" recorre todas las slides una sola vez (arranque).
		//  - "moved" sólo parchea la paginación (coste acotado), que es lo único
		//    que realmente cambia dinámicamente con role="tablist"/"tab".
		//  - Las escrituras son idempotentes (no se re-escriben si ya son válidas).
		//
		// IMPORTANT: must be registered BEFORE splide.mount() so the 'mounted'
		// event is caught — it fires during mount() and is missed if we attach
		// the listener afterwards.
		function patchSlideRoles() {
			splide.Components.Slides.forEach( function ( Slide ) {
				if ( Slide.slide.getAttribute( 'role' ) !== 'group' ) {
					Slide.slide.setAttribute( 'role', 'group' );
				}
				if ( Slide.slide.getAttribute( 'aria-roledescription' ) !== 'slide' ) {
					Slide.slide.setAttribute( 'aria-roledescription', 'slide' );
				}
			} );
		}

		splide.on( 'mounted', function () {
			patchSlideRoles();
			normalisePagination( el );
		} );

		// En "moved" sólo se actualiza la paginación. Las slides clonadas ya
		// tienen sus roles correctos desde el mount (Splide reutiliza los nodos).
		splide.on( 'moved', function () {
			normalisePagination( el );
		} );

		if ( ticker ) {
			// ── WCAG 2.2.2: botón de pausa/reproducción accesible ──────────────
			// El botón lo renderiza render.php (con las etiquetas ya traducidas
			// por Polylang vía data-*), para que el texto cambie de idioma sin
			// depender de wp.i18n en el front. Aquí solo conectamos el handler.
			if ( ! prefersReducedMotion ) {
				const pauseBtn = el.querySelector( '.waiis-slider__pause' );

				if ( pauseBtn ) {
					const pauseLabel = pauseBtn.getAttribute( 'data-pause-label' ) || 'Pausar animación';
					const playLabel  = pauseBtn.getAttribute( 'data-play-label' )  || 'Iniciar animación';
					const labelEl    = pauseBtn.querySelector( '.waiis-slider__pause-label' );

					// Estado propio de "detenido por el usuario", separado del
					// pausado temporal por hover. Así el botón no pelea con el
					// pauseOnHover de la extensión.
					let userStopped = false;

					const render = () => {
						pauseBtn.setAttribute( 'aria-pressed', userStopped ? 'true' : 'false' );
						if ( labelEl ) labelEl.textContent = userStopped ? playLabel : pauseLabel;
					};

					// El hover y el foco dentro de las diapositivas pausan el movimiento,
					// pero el botón queda fuera de esa zona para poder reanudarlo sin
					// que el propio hover vuelva a pausarlo inmediatamente.
					const track = el.querySelector( '.splide__track' );
					if ( track ) {
						const interaction = { pointer: false, focus: false };
						const syncInteractionPause = () => {
							if ( userStopped ) return;
							const autoScroll = splide.Components.AutoScroll;
							if ( ! autoScroll ) return;
							if ( interaction.pointer || interaction.focus ) {
								autoScroll.pause( false );
							} else {
								autoScroll.play();
							}
						};
						track.addEventListener( 'pointerenter', () => {
							interaction.pointer = true;
							syncInteractionPause();
						} );
						track.addEventListener( 'pointerleave', () => {
							interaction.pointer = false;
							syncInteractionPause();
						} );
						track.addEventListener( 'focusin', () => {
							interaction.focus = true;
							syncInteractionPause();
						} );
						track.addEventListener( 'focusout', ( event ) => {
							if ( ! track.contains( event.relatedTarget ) ) {
								interaction.focus = false;
								syncInteractionPause();
							}
						} );
					}

					pauseBtn.addEventListener( 'click', () => {
						const autoScroll = splide.Components.AutoScroll;
						if ( ! autoScroll ) return;

						userStopped = ! userStopped;
						if ( userStopped ) {
							autoScroll.pause(); // stop=true: pausa permanente hasta play()
						} else {
							autoScroll.play();  // reanuda y limpia flags hover/focus
						}
						render();
					} );

					render();
				}
			} else {
				// El autoplay está desactivado por preferencia del sistema; no
				// mostramos un control de pausa que no tendría ninguna acción.
				const pauseBtn = el.querySelector( '.waiis-slider__pause' );
				if ( pauseBtn ) pauseBtn.hidden = true;
			}

			// ── Lazy mount for tickers ─────────────────────────────────────────
			// Defer mounting until the ticker enters the viewport. Off-screen
			// auto-scroll carousels otherwise pay Splide's full mount cost (slide
			// cloning, layout) plus a per-frame requestAnimationFrame loop from
			// AutoScroll while invisible, harming scroll/load performance on pages
			// with multiple tickers. They have no arrows/pagination, so there is
			// nothing else to initialise for them.
			const viewportObserver = new IntersectionObserver( ( entries ) => {
				if ( entries.some( ( entry ) => entry.isIntersecting ) ) {
					viewportObserver.disconnect();
					splide.mount( { AutoScroll } );
				}
			}, { rootMargin: '200px 0px' } );
			viewportObserver.observe( el );
		} else {
			splide.mount( {} );

			// ── Trackpad horizontal scroll ────────────────────────────────────
			let wheelAccum   = 0;
			let wheelCooling = false;

			el.addEventListener(
				'wheel',
				( e ) => {
					// Only intercept predominantly horizontal scrolls.
					if ( Math.abs( e.deltaX ) <= Math.abs( e.deltaY ) ) return;

					e.preventDefault();

					if ( wheelCooling ) return;

					wheelAccum += e.deltaX;

					const threshold = 50;

					if ( Math.abs( wheelAccum ) >= threshold ) {
						splide.go( wheelAccum > 0 ? '+1' : '-1' );
						wheelAccum   = 0;
						wheelCooling = true;
						// Cooldown matches the slide transition speed.
						setTimeout( () => { wheelCooling = false; }, speed + 100 );
					}
				},
				{ passive: false }
			);

			// ── Controls bar ─────────────────────────────────────────────────────
			// Splide appends .splide__pagination and .splide__arrows as direct
			// children of the root element, with no shared wrapper.
			// We group them in a single .splide__controls div so CSS can lay them
			// out as one flex row: [bullets ←flex-1→] [←][→]
			//
			// On window resize Splide fires its `resized` event and may reorder
			// those elements back to the root, emptying the bar we built.
			// We rebuild it each time to keep the layout intact.
			const observerConfig = {
				childList:  true,
				subtree:    true,
				attributes: true,
				attributeFilter: [ 'role', 'aria-selected' ],
			};

			function buildControlsBar() {
				// Capture references BEFORE removing the wrapper, otherwise
				// existing.remove() would destroy the children too.
				const pagination = el.querySelector( '.splide__pagination' );
				const arrows     = el.querySelector( '.splide__arrows' );

				// Remove stale bar now that we hold references to its children.
				const existing = el.querySelector( '.splide__controls' );
				if ( existing ) existing.remove();

				if ( pagination || arrows ) {
					const bar     = document.createElement( 'div' );
					bar.className = 'splide__controls';
					el.appendChild( bar );
					if ( pagination ) bar.appendChild( pagination );
					if ( arrows )     bar.appendChild( arrows );
				}

				// Fallback: asegurar roles tras (re)construir controles.
				normalisePagination( el );
			}

			// ── Watch for Splide reinserting arrows/pagination on breakpoint change ──
			// When Splide crosses a breakpoint it may destroy and recreate the arrows
			// and pagination nodes, appending them again as direct children of the root.
			// MutationObserver fires asynchronously (microtask) after each DOM batch.
			const observer = new MutationObserver( ( mutations ) => {
				// Sólo reaccionar cuando Splide reinserta arrows/pagination como hijos
				// DIRECTOS de la raíz (parentNode === el). Si el nodo se añadió dentro
				// de nuestro propio .splide__controls, lo ignoramos.
				const needsRebuild = mutations.some( ( m ) =>
					[ ...m.addedNodes ].some(
						( n ) =>
							n.nodeType === Node.ELEMENT_NODE &&
							( n.classList.contains( 'splide__arrows' ) ||
							  n.classList.contains( 'splide__pagination' ) ) &&
							n.parentNode === el
					)
				);

				// Splide puede cambiar el "role" de una paginación ya existente
				// (tablist) al cruzar breakpoints, sin crear un nodo nuevo.
				const roleChanged = mutations.some(
					( m ) =>
						m.type === 'attributes' &&
						m.target && m.target.classList &&
						( m.target.classList.contains( 'splide__pagination' ) ||
						  m.target.classList.contains( 'splide__pagination__page' ) )
				);

				if ( needsRebuild ) {
					buildControlsBar();
				} else if ( roleChanged ) {
					// Splide reconstruye la paginación al cruzar breakpoints y
					// vuelve a asignar role="tablist"/"tab". Re-normalizamos aquí.
					normalisePagination( el );
				}
			} );

			observer.observe( el, observerConfig );

			buildControlsBar();
		}
	} );
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initSliders );
} else {
	initSliders();
}
